import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { type User, apiClient, invalidateApiCache, setCacheIdentity, setImpersonation } from "./api-client";
import { superAdminApi } from "./superadmin-api";
import { isLocale, setLocale, type Locale } from "../i18n";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  requestMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<User>;
  verifyOtp: (email: string, code: string) => Promise<User>;
  devLogin: (email: string) => Promise<User>;
  logout: () => Promise<void>;
  /** Moves this browser session to another club the user belongs to. */
  switchClub: (clubId: number) => Promise<void>;
  /** Super admin only: browse the app as another user, read-only. */
  /** userRef is a member's email (super admin) or "#<id>" (a parent's child). */
  startImpersonation: (userRef: string, clubId?: number | null) => Promise<User | null>;
  stopImpersonation: () => Promise<void>;
  /** Super admin only: enter a club as its admin (break-glass, audited), for a reason and a time. */
  enterClub: (clubId: number, reason: string, minutes: number) => Promise<User>;
  /** Ends break-glass access; the session falls back to the super admin's own clubs, if any. */
  leaveClub: () => Promise<void>;
  /** Switches the UI language now and saves it on the account (not while viewing as someone else). */
  changeLocale: (locale: Locale) => Promise<void>;
  /** Reloads the signed-in profile (roles change when a staff member starts or stops training). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000; // must stay below the backend's token_ttl
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // refresh this long before the access token actually expires

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // A saved account language wins over whatever the browser picked. The switch is
  // async now (a non-default locale is fetched on demand); a failed fetch just
  // leaves the UI in the language it booted with.
  useEffect(() => {
    if (isLocale(user?.locale)) {
      setLocale(user.locale).catch(() => undefined);
    }
  }, [user?.locale]);

  // The read cache holds server-localized names (grades among them). Tie it to the
  // signed-in account and its language so a payload read as one identity or
  // language is never reused for another.
  useEffect(() => {
    setCacheIdentity(user ? `${user.id}:${user.locale ?? ''}` : '');
  }, [user]);

  useEffect(() => {
    // A ?logout link lands here: drop the app session too.
    const signedOutElsewhere = new URLSearchParams(window.location.search).has('logout');
    (signedOutElsewhere ? apiClient.logout() : Promise.resolve()).then(() => apiClient.getCurrentUser()).then(async (storedUser) => {
      let user = storedUser;
      // The stored access token may already be stale (tab left open, app
      // reopened after a while); trade it in via the refresh cookie before
      // the first real API call gets a chance to bounce the user to /login.
      if (user) {
        await apiClient.refreshAccessToken();
        // Re-sync the stored profile with the server (roles or memberships may have
        // changed, and a closed tab can leave a stale "view as" profile behind).
        user = (await apiClient.refreshCurrentUser().catch(() => null)) ?? user;
      }
      setUser(user);
      setLoading(false);
    });
  }, []);

  // Silently exchange the refresh_token cookie for a new access token before
  // the current one expires, so a session survives idle tabs and deploys
  // without forcing the user back through the magic-link email flow.
  useEffect(() => {
    if (!user) {
      return;
    }

    const interval = setInterval(() => {
      apiClient.refreshAccessToken();
    }, ACCESS_TOKEN_TTL_MS - REFRESH_MARGIN_MS);

    return () => clearInterval(interval);
  }, [user]);

  const requestMagicLink = useCallback(async (email: string) => {
    await apiClient.requestMagicLink(email);
  }, []);

  const verifyMagicLink = useCallback(async (token: string) => {
    const user = await apiClient.verifyMagicLink(token);
    setUser(user);
    return user;
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    const user = await apiClient.verifyOtp(email, code);
    setUser(user);
    return user;
  }, []);

  const devLogin = useCallback(async (email: string) => {
    const user = await apiClient.devLogin(email);
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(async () => {
    await apiClient.logout();
    setUser(null);
  }, []);

  const switchClub = useCallback(async (clubId: number) => {
    setUser(await apiClient.switchClub(clubId));
  }, []);

  const startImpersonation = useCallback(async (userRef: string, clubId?: number | null) => {
    setImpersonation({ userRef, clubId: clubId ?? null });
    try {
      const target = await apiClient.refreshCurrentUser();
      // Switching without a page reload: the pages remount and refetch, never from the previous person's reads.
      invalidateApiCache();
      setUser(target);
      return target;
    } catch (err) {
      setImpersonation(null);
      throw err;
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    setImpersonation(null);
    setUser(await apiClient.refreshCurrentUser());
  }, []);

  const enterClub = useCallback(async (clubId: number, reason: string, minutes: number) => {
    await superAdminApi.enterClub(clubId, reason, minutes);
    const entered = await apiClient.switchClub(clubId);
    invalidateApiCache();
    setUser(entered);
    return entered;
  }, []);

  const leaveClub = useCallback(async () => {
    await superAdminApi.leaveClub();
    // The next token no longer finds the club behind the grant and falls back to the user's own.
    await apiClient.refreshAccessToken();
    invalidateApiCache();
    setUser(await apiClient.refreshCurrentUser());
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await apiClient.refreshCurrentUser());
  }, []);

  const changeLocale = useCallback(async (locale: Locale) => {
    await setLocale(locale);
    // Localized reads must be refetched in the new language. Done here rather than
    // relying on the account PATCH below, which is skipped while viewing as someone.
    invalidateApiCache();
    if (user && !user.impersonator) {
      const updated = await apiClient.updateLocale(locale);
      setUser((current) => (current ? { ...current, locale: updated.locale } : current));
    }
  }, [user]);

  // Stable object: without this every render handed the consumers a new value and
  // re-rendered all of them (30 files call useAuth()).
  const actions = useMemo(
    () => ({ requestMagicLink, verifyMagicLink, verifyOtp, devLogin, logout, switchClub, startImpersonation, stopImpersonation, enterClub, leaveClub, changeLocale, refreshUser }),
    [requestMagicLink, verifyMagicLink, verifyOtp, devLogin, logout, switchClub, startImpersonation, stopImpersonation, enterClub, leaveClub, changeLocale, refreshUser],
  );

  // Only user/loading (and the actions they depend on) can change this.
  const value = useMemo<AuthContextType>(
    () => ({ user, loading, ...actions }),
    [user, loading, actions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

